/**
 * DateRangeInput Component
 * Dual input for selecting date range (From and To dates)
 * Includes validation to ensure From ≤ To and no future dates
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 3 (AC-3)
 */

import * as React from 'react';
import { Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// ============================================
// INTERFACE
// ============================================

export interface DateRangeInputProps {
  fromDate: string | null;
  toDate: string | null;
  onDateRangeChange: (from: string | null, to: string | null) => void;
  label?: string;
  className?: string;
}

// ============================================
// UTILITIES
// ============================================

/**
 * Get today's date in YYYY-MM-DD format (max allowed date)
 */
function getTodayISO(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * Validate date range
 */
function validateDateRange(from: string | null, to: string | null): string | null {
  if (!from && !to) return null;

  const today = getTodayISO();
  const fromDate = from ? new Date(from) : null;
  const toDate = to ? new Date(to) : null;
  const todayDate = new Date(today);

  // Check if from date is in the future
  if (fromDate && fromDate > todayDate) {
    return 'From date cannot be in the future';
  }

  // Check if to date is in the future
  if (toDate && toDate > todayDate) {
    return 'To date cannot be in the future';
  }

  // Check if from date is after to date
  if (fromDate && toDate && fromDate > toDate) {
    return 'From date must be before or equal to To date';
  }

  return null;
}

/**
 * Format date for display (locale-aware)
 */
function formatDateForDisplay(dateStr: string | null): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================
// COMPONENT
// ============================================

export const DateRangeInput = React.memo(function DateRangeInput({
  fromDate,
  toDate,
  onDateRangeChange,
  label = 'Date Range',
  className,
}: DateRangeInputProps) {
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const maxDate = getTodayISO();

  // Validate whenever dates change
  React.useEffect(() => {
    const error = validateDateRange(fromDate, toDate);
    setValidationError(error);
  }, [fromDate, toDate]);

  // Handle from date change
  const handleFromDateChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFrom = e.target.value || null;
      onDateRangeChange(newFrom, toDate);
    },
    [onDateRangeChange, toDate]
  );

  // Handle to date change
  const handleToDateChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTo = e.target.value || null;
      onDateRangeChange(fromDate, newTo);
    },
    [onDateRangeChange, fromDate]
  );

  // Clear date range
  const handleClear = React.useCallback(() => {
    onDateRangeChange(null, null);
  }, [onDateRangeChange]);

  const hasValue = fromDate || toDate;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Label */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-foreground">
          <Calendar className="mr-1.5 inline-block h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {label}
        </Label>
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear date range"
          >
            Clear
          </button>
        )}
      </div>

      {/* Date Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* From Date */}
        <div className="space-y-1.5">
          <Label htmlFor="from-date" className="text-xs text-muted-foreground">
            From Date
          </Label>
          <Input
            id="from-date"
            type="date"
            value={fromDate || ''}
            onChange={handleFromDateChange}
            max={maxDate}
            className={cn(
              'text-sm',
              validationError && 'border-destructive focus-visible:ring-destructive'
            )}
            aria-invalid={!!validationError}
            aria-describedby={validationError ? 'date-range-error' : undefined}
          />
        </div>

        {/* To Date */}
        <div className="space-y-1.5">
          <Label htmlFor="to-date" className="text-xs text-muted-foreground">
            To Date
          </Label>
          <Input
            id="to-date"
            type="date"
            value={toDate || ''}
            onChange={handleToDateChange}
            max={maxDate}
            min={fromDate || undefined}
            className={cn(
              'text-sm',
              validationError && 'border-destructive focus-visible:ring-destructive'
            )}
            aria-invalid={!!validationError}
            aria-describedby={validationError ? 'date-range-error' : undefined}
          />
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <p
          id="date-range-error"
          className="text-xs text-destructive flex items-center gap-1"
          role="alert"
        >
          <span className="font-medium">Error:</span>
          {validationError}
        </p>
      )}

      {/* Selected Range Display (for accessibility and visual feedback) */}
      {fromDate && toDate && !validationError && (
        <p className="text-xs text-muted-foreground" role="status">
          Selected: {formatDateForDisplay(fromDate)} - {formatDateForDisplay(toDate)}
        </p>
      )}

      {/* Accessibility announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {validationError
          ? `Date range error: ${validationError}`
          : hasValue
            ? `Date range selected: ${fromDate ? formatDateForDisplay(fromDate) : 'Not set'} to ${toDate ? formatDateForDisplay(toDate) : 'Not set'}`
            : 'No date range selected'}
      </div>
    </div>
  );
});
